import * as s from "superstruct";

export const getSlugPrefixByPageType = (args: {
  pageType: string | null;
  parent?:
    | "resources"
    | "journal"
    | "spendTrends"
    | "tools"
    | "templates"
    | null;
  group?: string | null;
  slug?: string | null;
}) => {
  const { pageType, parent, slug, group } = args;
  const prependGroup =
    group && !slug?.includes(`${group}/`) ? `${group}/` : null;

  switch (pageType) {
    case "corePage":
    case "paidPage":
    case "partnerPage":
    case "homePage":
    case "sandboxPage":
    case "staticUrl":
    case "authorsPage":
      return "";
    case "contentHubArticle":
    case "journalBundlePage": {
      // Default parent to "journal" when undefined, following the same pattern as getTypeAndParentForPageId
      const defaultedParent = parent ?? "journal";

      // We have a journal homepage and a resources homepage which includes a little bit of special behavior
      if (
        !defaultedParent &&
        ![
          "resources",
          "journal",
          "spendTrends",
          "tools",
          "templates",
          "home",
        ].includes(slug ?? "")
      ) {
        throw new Error(
          `Cannot get url for ${pageType} without parent ${slug}`,
        );
      }
      if (slug === "home") {
        return "journal/";
      }
      if (slug === "resources") {
        return "resources/";
      }
      if (slug === "spend-trends") {
        return "spend-trends/";
      }
      if (defaultedParent === "spendTrends") {
        if (prependGroup) {
          return "spend-trends/" + prependGroup;
        }
        return "spend-trends/";
      }
      if (slug === "tools") {
        return "tools/";
      }
      if (slug === "templates") {
        return "templates/";
      }
      return `${defaultedParent}/`;
    }
    case "pressRelease":
      return "journal/press/";
    case "productLogPost":
      return "product-announcements/";
    case "legalPage":
      return "legal/";
    case "helpCenterArticle":
    case "helpCenterCategory":
      return "support/";
    case "signupPage":
      return "signup/";
    default:
      throw new Error(`No url definition found for pageType ${pageType}`);
  }
};

export const urlFromSlugSchema = s.object({
  pageType: s.nullable(s.string()),
  slug: s.nullable(s.string()),
  parent: s.optional(
    s.nullable(
      s.enums(["resources", "journal", "spendTrends", "tools", "templates"]),
    ),
  ),
  group: s.optional(s.nullable(s.string())),
});

export type UrlFromSlugOptions = s.Infer<typeof urlFromSlugSchema>;

export const buildUrlFromSlug = (args: UrlFromSlugOptions) => {
  const { pageType, slug, parent, group } = args;

  const prefix = getSlugPrefixByPageType({ pageType, parent, slug, group });

  return `/${prefix + slug}`;
};
